import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { dealRoomApi } from '../lib/api';
import { formatRelativeTime, cn } from '../lib/utils';
import { Plus, Briefcase, FileText, MessageSquare, ChevronRight, Link } from 'lucide-react';

const DealRoomsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dealRooms, setDealRooms] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newDealRoom, setNewDealRoom] = useState({ name: '', company_name: '' });
  const [creating, setCreating] = useState(false);

  const fetchDealRooms = async () => {
    setLoading(true);
    try {
      const response = await dealRoomApi.list();
      setDealRooms(response.data.deal_rooms || []);
    } catch (err) {
      console.error('Failed to fetch deal rooms:', err);
      setDealRooms([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDealRooms();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const response = await dealRoomApi.create(newDealRoom);
      setDealRooms([response.data, ...dealRooms]);
      setShowCreateModal(false);
      setNewDealRoom({ name: '', company_name: '' });
      // Navigate to the new deal room
      navigate(`/deal-rooms/${response.data.deal_room_id}`);
    } catch (err) {
      console.error('Failed to create deal room:', err);
    } finally {
      setCreating(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-emerald-400 bg-emerald-400/10';
      case 'passed': return 'text-rose-400 bg-rose-400/10';
      case 'invested': return 'text-blue-400 bg-blue-400/10';
      default: return 'text-zinc-400 bg-zinc-400/10';
    }
  };

  return (
    <MainLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-light font-['Outfit'] text-zinc-50">Deal Rooms</h1>
            <p className="text-zinc-400 font-['Manrope']">Upload decks, notes, and links. Get instant analysis.</p>
          </div>
          <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
            <DialogTrigger asChild>
              <Button 
                data-testid="create-deal-room-btn"
                className="bg-white text-zinc-900 hover:bg-zinc-200"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Deal Room
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800">
              <DialogHeader>
                <DialogTitle className="text-zinc-50 font-['Outfit']">Create Deal Room</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 mt-4">
                <div>
                  <label className="text-sm text-zinc-400 font-['Manrope'] mb-1 block">Deal Room Name</label>
                  <Input
                    value={newDealRoom.name}
                    onChange={(e) => setNewDealRoom({ ...newDealRoom, name: e.target.value })}
                    placeholder="e.g., Acme Series A Review"
                    required
                    data-testid="input-deal-room-name"
                    className="bg-zinc-800 border-zinc-700 text-zinc-50"
                  />
                </div>
                <div>
                  <label className="text-sm text-zinc-400 font-['Manrope'] mb-1 block">Company Name (optional)</label>
                  <Input
                    value={newDealRoom.company_name}
                    onChange={(e) => setNewDealRoom({ ...newDealRoom, company_name: e.target.value })}
                    placeholder="Acme Inc"
                    data-testid="input-company-name"
                    className="bg-zinc-800 border-zinc-700 text-zinc-50"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowCreateModal(false)}
                    className="border-zinc-700 text-zinc-400"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit"
                    disabled={creating || !newDealRoom.name}
                    data-testid="submit-deal-room-btn"
                    className="bg-white text-zinc-900 hover:bg-zinc-200"
                  >
                    {creating ? 'Creating...' : 'Create'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Deal Rooms Grid */}
        {loading ? (
          <div className="grid grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-md p-5 animate-pulse">
                <div className="h-5 w-32 bg-zinc-800 rounded mb-3" />
                <div className="h-4 w-24 bg-zinc-800 rounded mb-4" />
                <div className="flex gap-4">
                  <div className="h-4 w-16 bg-zinc-800 rounded" />
                  <div className="h-4 w-16 bg-zinc-800 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : dealRooms.length > 0 ? (
          <div className="grid grid-cols-3 gap-4">
            {dealRooms.map((room) => (
              <div
                key={room.deal_room_id}
                onClick={() => navigate(`/deal-rooms/${room.deal_room_id}`)}
                data-testid={`deal-room-card-${room.deal_room_id}`}
                className="bg-zinc-900 border border-zinc-800 rounded-md p-5 cursor-pointer hover:border-zinc-700 transition-colors group"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-zinc-50 font-medium font-['Outfit'] truncate">{room.name}</h3>
                    {room.company_name && (
                      <p className="text-zinc-400 text-sm font-['Manrope']">{room.company_name}</p>
                    )}
                  </div>
                  <ChevronRight className="w-5 h-5 text-zinc-600 group-hover:text-zinc-400 transition-colors" />
                </div>

                <div className="flex items-center gap-4 mb-3">
                  <div className="flex items-center gap-1.5 text-zinc-500 text-xs">
                    <FileText className="w-3.5 h-3.5" />
                    <span className="font-mono">{room.document_ids?.length || 0}</span>
                  </div>
                  <span className={cn("px-2 py-0.5 text-xs font-mono rounded-full", getStatusColor(room.status))}>
                    {room.status}
                  </span>
                </div>

                <p className="text-zinc-500 text-xs font-mono">
                  Updated {formatRelativeTime(room.updated_at)}
                </p>

                {room.dashboard && (
                  <div className="mt-3 pt-3 border-t border-zinc-800/50">
                    <p className="text-zinc-400 text-xs line-clamp-2">{room.dashboard.company_summary}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-zinc-900 border border-zinc-800 rounded-md p-12 text-center">
            <div className="w-16 h-16 bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Briefcase className="w-8 h-8 text-zinc-500" />
            </div>
            <h3 className="text-lg font-medium font-['Outfit'] text-zinc-50 mb-2">No deal rooms yet</h3>
            <p className="text-zinc-400 font-['Manrope'] mb-6 max-w-md mx-auto">
              Create a deal room to upload pitch decks, meeting notes, or links and get instant AI-powered analysis.
            </p>
            <Button
              onClick={() => setShowCreateModal(true)}
              data-testid="empty-create-deal-room"
              className="bg-white text-zinc-900 hover:bg-zinc-200"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Deal Room
            </Button>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default DealRoomsPage;
